/**
 * A busca do `SearchSelect` — pura, para poder ser testada sem navegador.
 *
 * Está aqui e não no componente pelo mesmo motivo de `lib/eco.ts` e
 * `lib/traffic.ts`: o Node roda `.ts` tirando os tipos, mas **não roda `.tsx`**
 * (JSX não é tipo, é sintaxe nova). Lógica dentro do componente é lógica que só
 * um navegador consegue exercitar — e aí ela deixa de ser exercitada.
 */

/** Uma opção da lista. `hint` é a segunda linha: placa, código, o que desempata. */
export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly hint?: string | undefined;
}

/**
 * Texto comparável: minúsculo e sem acento.
 *
 * `NFD` separa a letra do acento, e a faixa `̀-ͯ` tira os acentos —
 * sem depender de `\p{Diacritic}`, que exige flag `u` e nem todo navegador de
 * campo tinha quando isto foi escrito.
 */
export function foldText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();
}

/**
 * As opções que casam com o termo, **na ordem recebida**.
 *
 * Casa em qualquer posição do rótulo e da dica: o `type-ahead` do `<select>`
 * nativo casa só pelo começo, então procurar "900" em "Ônibus 900" não acha
 * nada — e é exatamente assim que alguém procura um ônibus. Quem procura por
 * placa digita a placa, daí a dica entrar na comparação.
 *
 * A ordem não é reembaralhada por relevância: a lista chega ordenada por quem a
 * montou (código da base, nome do veículo), e isso foi escolha de alguém.
 */
export function filterOptions(options: readonly SelectOption[], term: string): readonly SelectOption[] {
  const needle = foldText(term.trim());
  if (!needle) return options;
  return options.filter((o) => foldText(`${o.label} ${o.hint ?? ""}`).includes(needle));
}
